/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useMemo, useState } from 'react'

import { Button, Icon, Icons } from '@clientbase/clientbase-library'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Input,
  Modal,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { isGroupMode } from 'lib/merchant-group'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { GradientOpenWithIcon } from 'components/GradientIcon/GradientIcon'
import MerchantBadge from 'components/MerchantBadge'
import Status from 'components/Status'
import { StatusKeys } from 'components/Status/Status'

import { RecentItem } from 'utils/searchHistory'

import { theme } from 'styles/theme'

import * as S from './SearchModal.styles'

interface SearchModalProps {
  isOpen: boolean
  handleCloseFunction: () => void
  placeholder: string
  inputValue: string
  handleInputChangeFunction: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
  clearInputFunction: () => void
  results: ResultType[]
  loadingResults: boolean
  // Sugestão do Elasticsearch para "Você quis dizer...?" quando a busca não
  // retorna resultados; ao clicar, refaz a busca com o termo sugerido.
  suggestion?: string
  onApplySuggestion?: (term: string) => void
  // Chamado quando o usuário abre qualquer resultado (clique ou Enter), para
  // gravar o termo da busca e o item acessado no histórico de recentes.
  onResultOpen?: (result: ResultType) => void
  // Histórico (localStorage) mostrado quando a busca abre com o input vazio.
  recentTerms?: string[]
  recentItems?: RecentItem[]
  onApplyRecentTerm?: (term: string) => void
  onRecentItemClick?: (item: RecentItem) => void
}

export type ResultType = {
  title: string
  // Título com o termo buscado destacado (<mark>...</mark>) vindo do Elasticsearch
  titleHighlight?: string
  nickname?: string
  subtitle: string
  type: InfoKeys
  status?: string
  // Label de status pré-resolvido (usado quando o status não existe no
  // statusMapper do componente Status, ex.: contas a pagar)
  statusLabel?: string
  category?: string
  dueDate?: string
  // Ícone/imagem real do plano (FontAwesome icon name ou URL de thumbnail)
  productIcon?: string
  productThumbnail?: string
  // "título: valor" do campo extra que casou com a busca (com <mark>),
  // mostrado para o usuário saber por que o cliente apareceu
  customFieldMatch?: string
  // Página do registro quando não é a padrão do tipo (ex.: cliente achado pela
  // anotação abre direto na aba Relacionamento)
  href?: string
  // eslint-disable-next-line @typescript-eslint/ban-types
  clickFunction: Function
  uuid?: string
  // SC-10999: empresa do resultado na busca da visão multi-empresas
  merchant?: { uuid?: string; name: string; logo_url?: string | null }
}

const resultTypeInfos: Record<
  string,
  { label: string; emptyLabel: string; icon: Icons }
> = {
  client: {
    label: 'Clientes',
    emptyLabel: 'nenhum cliente',
    icon: 'person'
  },
  billing: {
    label: 'Cobranças',
    emptyLabel: 'nenhuma cobrança',
    icon: 'paid'
  },
  recurrence: {
    label: 'Recorrências',
    emptyLabel: 'nenhuma recorrência',
    icon: 'cached'
  },
  product: {
    label: 'Planos',
    emptyLabel: 'nenhum plano',
    icon: 'storefront'
  },
  accounts_payable: {
    label: 'Contas a pagar',
    emptyLabel: 'nenhuma conta a pagar',
    icon: 'receipt'
  },
  nfse: {
    label: 'Notas fiscais',
    emptyLabel: 'nenhuma nota fiscal',
    icon: 'description'
  },
  contract: {
    label: 'Contratos',
    emptyLabel: 'nenhum contrato',
    icon: 'assignment'
  }
}

type InfoKeys =
  | 'client'
  | 'billing'
  | 'recurrence'
  | 'product'
  | 'accounts_payable'
  | 'nfse'
  | 'contract'

// 'all' = chip "Tudo" (default): mostra seções com top N de cada tipo.
type FilterKey = 'all' | InfoKeys

const TAB_ORDER: InfoKeys[] = [
  'client',
  'billing',
  'recurrence',
  'product',
  'accounts_payable',
  'nfse',
  'contract'
]

// Quantos itens por seção na visão "Tudo" antes do "Ver todas →".
const ALL_VIEW_SECTION_LIMIT = 3

const getInitials = (name: string) => {
  const words = name.trim().split(' ')
  if (words.length >= 2) {
    return `${words[0][0]}${words[words.length - 1][0]}`
  }
  return words[0]?.slice(0, 2) || '?'
}

// Renderiza o título realçando o termo buscado. O highlight vem do Elasticsearch
// no formato "texto <mark>termo</mark> texto"; fazemos o split nas tags <mark>
// e renderizamos cada pedaço como nó React (sem dangerouslySetInnerHTML, evitando XSS).
const renderHighlight = (fallback: string, highlight?: string) => {
  if (!highlight) return fallback

  const parts = highlight.split(/(<mark>|<\/mark>)/)
  let inMark = false
  return parts.map((part, i) => {
    if (part === '<mark>') {
      inMark = true
      return null
    }
    if (part === '</mark>') {
      inMark = false
      return null
    }
    if (!part) return null
    return inMark ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>
  })
}

// Avatar do resultado:
// - cliente: círculo com iniciais (identidade pessoal)
// - plano: imagem/ícone real do produto (thumbnail upado ou icon do plano)
// - demais (cobrança, recorrência, conta a pagar): ícone do tipo num quadrado
const ResultAvatar = ({ result }: { result: ResultType }) => {
  if (result.type === 'client') {
    return <S.Avatar>{getInitials(result.title)}</S.Avatar>
  }

  if (result.type === 'product') {
    return (
      <S.IconBox>
        {result.productThumbnail ? (
          <Image
            src={result.productThumbnail}
            alt={result.title}
            width={32}
            height={32}
            style={{ borderRadius: '6px', objectFit: 'contain' }}
          />
        ) : (
          <GradientOpenWithIcon
            icon={(result.productIcon || 'storefront') as Icons}
            width="22px"
            height="22px"
            colors={['#00EC89', '#00F4F5', '#00FEFF']}
          />
        )}
      </S.IconBox>
    )
  }

  return (
    <S.IconBox>
      <Icon
        icon={resultTypeInfos[result.type].icon}
        sx={{ fontSize: 22, color: theme.palette.neutral[400] }}
      />
    </S.IconBox>
  )
}

// Larguras variadas dos chips-esqueleto da barra de filtros (imitam "Tudo",
// "Clientes (230)", etc. sem revelar contadores que ainda não chegaram).
const SKELETON_CHIP_WIDTHS = [52, 88, 96, 110, 70, 120, 100]

// Larguras variadas por linha para o skeleton parecer mais orgânico
const SKELETON_TITLE_WIDTHS = ['45%', '60%', '38%', '52%', '48%', '64%', '42%']
const SKELETON_SUBTITLE_WIDTHS = [
  '70%',
  '55%',
  '78%',
  '62%',
  '72%',
  '58%',
  '68%'
]

const ResultSkeleton = ({ index = 0 }: { index?: number }) => (
  <S.SkeletonResult>
    <S.SkeletonContent>
      <S.SkeletonAvatar />
      <S.SkeletonDetails>
        <S.SkeletonText width={SKELETON_TITLE_WIDTHS[index % 7]} />
        <S.SkeletonText width={SKELETON_SUBTITLE_WIDTHS[index % 7]} />
      </S.SkeletonDetails>
    </S.SkeletonContent>
    <S.SkeletonChip />
  </S.SkeletonResult>
)

const SearchModal = ({
  isOpen,
  handleCloseFunction,
  placeholder,
  inputValue,
  handleInputChangeFunction,
  clearInputFunction,
  results,
  loadingResults,
  suggestion,
  onApplySuggestion,
  onResultOpen,
  recentTerms = [],
  recentItems = [],
  onApplyRecentTerm,
  onRecentItemClick
}: SearchModalProps) => {
  const { push } = useRouter()
  // 'all' (Tudo) é o filtro default; os demais filtram por um tipo só.
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all')
  // Índice do resultado selecionado pela navegação por teclado (-1 = nenhum).
  const [activeIndex, setActiveIndex] = useState(-1)
  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })

  const grouped = useMemo(() => {
    const acc: Record<InfoKeys, ResultType[]> = {
      client: [],
      billing: [],
      recurrence: [],
      product: [],
      accounts_payable: [],
      nfse: [],
      contract: []
    }
    results.forEach((result) => {
      if (acc[result.type]) acc[result.type].push(result)
    })
    return acc
  }, [results])

  // Sempre que a busca é aberta, volta para o filtro "Tudo".
  useEffect(() => {
    if (isOpen) setActiveFilter('all')
  }, [isOpen])

  // Tipos com resultado, na ordem canônica (usado para chips e seções).
  const typesWithResults = useMemo(
    () => TAB_ORDER.filter((key) => grouped[key].length > 0),
    [grouped]
  )

  // Lista achatada do que está visível agora, na ordem em que aparece na tela —
  // base para a navegação por teclado (↑↓ percorre exatamente o que se vê).
  // Na visão "Tudo", respeita o limite de itens por seção.
  const flatVisible = useMemo(() => {
    if (activeFilter === 'all') {
      return typesWithResults.flatMap((key) =>
        grouped[key].slice(0, ALL_VIEW_SECTION_LIMIT)
      )
    }
    return grouped[activeFilter] || []
  }, [activeFilter, grouped, typesWithResults])

  // Zera a seleção do teclado quando os resultados ou o filtro mudam.
  useEffect(() => {
    setActiveIndex(-1)
  }, [results, activeFilter])

  // Mantém a linha ativa visível ao navegar com as setas.
  useEffect(() => {
    if (activeIndex < 0) return
    const node = document.querySelector('[data-active="true"]')
    node?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  // Navegação por teclado: ↑↓ percorre resultados e Enter abre o selecionado.
  // ←→ NÃO trocam de chip de propósito: com o foco no input precisam mover o
  // cursor entre as letras digitadas (os chips são trocados pelo clique). Esc
  // já é tratado pelo Modal.
  const handleModalKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!flatVisible.length) return
      setActiveIndex((i) => (i + 1) % flatVisible.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!flatVisible.length) return
      setActiveIndex((i) => (i <= 0 ? flatVisible.length - 1 : i - 1))
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && flatVisible[activeIndex]) {
        e.preventDefault()
        openResult(flatVisible[activeIndex])
      }
    }
  }

  const handleClientClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    result: ResultType
  ) => {
    // SC-10999: no grupo a ficha é aberta trocando para a empresa do cliente
    // (clickFunction); a navegação direta do <Link> cairia no aviso de área
    // por empresa
    if (isGroupMode()) e.preventDefault()
    // clickFunction navega; onResultOpen grava o termo e o item no histórico.
    result.clickFunction()
    onResultOpen?.(result)
    if (!e.ctrlKey) {
      handleCloseFunction()
    } else {
      e.preventDefault()
      window.open(result.href ?? `/clientes/${result.uuid}`, '_blank')
    }
  }

  // Abre o resultado (navega ou dispara o modal). Usado pelo clique e pelo Enter.
  const openResult = (result: ResultType) => {
    onResultOpen?.(result)
    if (result.type === 'client' && result.uuid) {
      // clickFunction grava o recente; o push faz a navegação (no grupo quem
      // navega é o clickFunction, trocando para a empresa do cliente).
      result.clickFunction()
      if (!isGroupMode()) push(result.href ?? `/clientes/${result.uuid}`)
      handleCloseFunction()
      return
    }
    result.clickFunction()
    handleCloseFunction()
  }

  // flatIndex: posição do item na lista achatada (flatVisible), usada para casar
  // com a seleção do teclado. -1 quando o item não participa da navegação.
  const renderResultRow = (
    result: ResultType,
    index: number,
    flatIndex = -1
  ) => {
    const content = (
      <>
        <S.ResultContent>
          <ResultAvatar result={result} />
          <S.ResultDetails>
            <S.ResultRow>
              <S.ResultTitle>
                {renderHighlight(result.title, result.titleHighlight)}
              </S.ResultTitle>
              {!!result.nickname && (
                <S.ResultNickname>{result.nickname}</S.ResultNickname>
              )}
            </S.ResultRow>
            <S.ResultSubtitle>{result.subtitle}</S.ResultSubtitle>
            {!!result.merchant && (
              <MerchantBadge
                name={result.merchant.name}
                logoUrl={result.merchant.logo_url}
                size={16}
              />
            )}
            {!!result.customFieldMatch && (
              <S.CustomFieldMatch>
                {renderHighlight(
                  result.customFieldMatch.replace(/<\/?mark>/g, ''),
                  result.customFieldMatch
                )}
              </S.CustomFieldMatch>
            )}
          </S.ResultDetails>
        </S.ResultContent>
        {result.type === 'client' &&
          result.category &&
          S.getCategoryLabel(result.category) && (
            <S.CategoryChip category={result.category}>
              {S.getCategoryLabel(result.category)}
            </S.CategoryChip>
          )}
        {result.type !== 'client' &&
          (!!result.status || !!result.statusLabel) && (
            <S.StatusChipWrap>
              <Status
                status={(result.status || 'pending') as StatusKeys}
                label={result.statusLabel}
                dueDate={result.dueDate as string}
              />
            </S.StatusChipWrap>
          )}
      </>
    )

    const isActive = flatIndex >= 0 && flatIndex === activeIndex

    if (result.type === 'client') {
      return (
        <Link
          key={result.uuid || `${result.type}-${index}`}
          href={result.href ?? `/clientes/${result.uuid}`}
          prefetch
          passHref
          legacyBehavior
        >
          <S.Result
            isActive={isActive}
            data-active={isActive || undefined}
            onMouseEnter={() => flatIndex >= 0 && setActiveIndex(flatIndex)}
            onClick={(e) => handleClientClick(e, result)}
          >
            {content}
          </S.Result>
        </Link>
      )
    }

    return (
      <S.Result
        key={result.uuid || `${result.type}-${index}`}
        isActive={isActive}
        data-active={isActive || undefined}
        onMouseEnter={() => flatIndex >= 0 && setActiveIndex(flatIndex)}
        onClick={() => {
          onResultOpen?.(result)
          result.clickFunction()
          handleCloseFunction()
        }}
      >
        {content}
      </S.Result>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleCloseFunction}>
      <S.Container>
        <S.Content onKeyDown={handleModalKeyDown}>
          {!isMobile && (
            <Tooltip
              enterTouchDelay={0}
              title="Fechar Pesquisa"
              placement="top"
            >
              <S.CloseButton size="small" onClick={() => handleCloseFunction()}>
                <Icon icon="close" fontSize="small" />
              </S.CloseButton>
            </Tooltip>
          )}

          <S.SearchBox hasResults={results.length > 0}>
            {loadingResults ? (
              <FontAwesomeIcon
                icon={faSpinner}
                className="spinner"
                style={{
                  marginRight: '8px'
                }}
                width={50}
                height={50}
              />
            ) : !isMobile ? (
              <Icon icon="search" fontSize="large" />
            ) : (
              <Image
                src="/only-icon.png"
                width={50}
                height={50}
                alt="Logo ClientBase"
              />
            )}

            {isMobile ? (
              <S.MobileSearchBox>
                <Input
                  placeholder={placeholder}
                  disableUnderline
                  value={inputValue}
                  onChange={handleInputChangeFunction}
                  sx={{ width: '100%' }}
                  autoFocus
                />

                {inputValue !== '' && (
                  <Tooltip
                    enterTouchDelay={0}
                    title="Limpar resultados"
                    placement="left"
                    hidden={inputValue == ''}
                  >
                    <Button variantButton="ghost" onClick={clearInputFunction}>
                      Limpar
                    </Button>
                  </Tooltip>
                )}
              </S.MobileSearchBox>
            ) : (
              <>
                <Input
                  placeholder={placeholder}
                  disableUnderline
                  value={inputValue}
                  onChange={handleInputChangeFunction}
                  sx={{ width: '100%', fontSize: '16px' }}
                  autoFocus
                />

                {inputValue !== '' && (
                  <Tooltip
                    enterTouchDelay={0}
                    title="Limpar resultados"
                    placement="left"
                  >
                    <Button variantButton="ghost" onClick={clearInputFunction}>
                      Limpar
                    </Button>
                  </Tooltip>
                )}
              </>
            )}

            {isMobile && (
              <Tooltip
                enterTouchDelay={0}
                title="Fechar Pesquisa"
                placement="top"
              >
                <S.CloseButton
                  size="small"
                  onClick={() => handleCloseFunction()}
                >
                  <Icon icon="close" fontSize="small" />
                </S.CloseButton>
              </Tooltip>
            )}
          </S.SearchBox>

          {/* Busca vazia: mostra histórico (termos buscados e itens acessados,
              de qualquer tipo) em vez de um modal vazio. */}
          {inputValue === '' &&
            (recentTerms.length > 0 || recentItems.length > 0) && (
              <S.RecentContainer>
                <S.RecentList>
                  {recentTerms.length > 0 && (
                    <>
                      <S.SectionHeader>
                        <Icon icon="cached" sx={{ fontSize: 16 }} />
                        Buscas recentes
                      </S.SectionHeader>
                      <S.RecentTermsRow>
                        {recentTerms.map((term) => (
                          <S.FilterChip
                            key={term}
                            type="button"
                            isActive={false}
                            onClick={() => onApplyRecentTerm?.(term)}
                          >
                            {term}
                          </S.FilterChip>
                        ))}
                      </S.RecentTermsRow>
                    </>
                  )}

                  {recentItems.length > 0 && (
                    <>
                      <S.SectionHeader>
                        <Icon icon="assignment" sx={{ fontSize: 16 }} />
                        Itens recentes
                      </S.SectionHeader>
                      {recentItems.map((item) => (
                        <S.Result
                          key={`${item.type}-${item.uuid}`}
                          onClick={() => onRecentItemClick?.(item)}
                        >
                          <S.ResultContent>
                            <ResultAvatar
                              result={{
                                title: item.title,
                                subtitle: item.subtitle || '',
                                type: item.type,
                                clickFunction: () => undefined
                              }}
                            />
                            <S.ResultDetails>
                              <S.ResultTitle>{item.title}</S.ResultTitle>
                              {!!item.subtitle && (
                                <S.ResultSubtitle>
                                  {item.subtitle}
                                </S.ResultSubtitle>
                              )}
                            </S.ResultDetails>
                          </S.ResultContent>
                        </S.Result>
                      ))}
                    </>
                  )}
                </S.RecentList>
              </S.RecentContainer>
            )}

          <S.ResultsContainer hasResults={inputValue !== ''}>
            <S.ResultsList>
              <S.FilterBar>
                {/* Enquanto carrega, mostra chips-esqueleto no lugar dos filtros
                    (não dá pra saber os contadores ainda); larguras variadas
                    para parecer mais orgânico. */}
                {loadingResults
                  ? SKELETON_CHIP_WIDTHS.map((width, i) => (
                      <S.SkeletonChip key={i} style={{ width }} />
                    ))
                  : (['all', ...TAB_ORDER] as FilterKey[]).map((key) => {
                      const count =
                        key === 'all' ? results.length : grouped[key].length
                      // Esconde chips de tipos sem resultado (exceto "Tudo"),
                      // evitando uma fileira enorme de filtros vazios.
                      if (key !== 'all' && count === 0) return null
                      const label =
                        key === 'all' ? 'Tudo' : resultTypeInfos[key].label
                      return (
                        <S.FilterChip
                          key={key}
                          type="button"
                          isActive={activeFilter === key}
                          onClick={() => setActiveFilter(key)}
                        >
                          {`${label}${count ? ` (${count})` : ''}`}
                        </S.FilterChip>
                      )
                    })}
              </S.FilterBar>

              {loadingResults &&
                Array.from({ length: 7 }).map((_, i) => (
                  <ResultSkeleton key={i} index={i} />
                ))}

              {/* Visão "Tudo": seções por tipo com top N + "Ver todas →". O
                  flatIndex acompanha flatVisible para a navegação por teclado. */}
              {!loadingResults &&
                activeFilter === 'all' &&
                (() => {
                  let cursor = -1
                  return typesWithResults.map((key) => {
                    const items = grouped[key].slice(0, ALL_VIEW_SECTION_LIMIT)
                    const total = grouped[key].length
                    return (
                      <Box key={key}>
                        <S.SectionHeader>
                          <Icon
                            icon={resultTypeInfos[key].icon}
                            sx={{ fontSize: 16 }}
                          />
                          {resultTypeInfos[key].label}
                        </S.SectionHeader>
                        {items.map((result, index) => {
                          cursor += 1
                          return renderResultRow(result, index, cursor)
                        })}
                        {total > ALL_VIEW_SECTION_LIMIT && (
                          <S.SectionSeeAll
                            type="button"
                            onClick={() => setActiveFilter(key)}
                          >
                            {`Ver ${resultTypeInfos[
                              key
                            ].label.toLowerCase()} (${total}) →`}
                          </S.SectionSeeAll>
                        )}
                      </Box>
                    )
                  })
                })()}

              {/* Filtro específico: lista plana só daquele tipo. */}
              {!loadingResults &&
                activeFilter !== 'all' &&
                grouped[activeFilter].map((result, index) =>
                  renderResultRow(result, index, index)
                )}

              {!loadingResults && !flatVisible.length && (
                <S.EmptyState>
                  <Icon
                    icon={
                      activeFilter === 'all'
                        ? 'search'
                        : resultTypeInfos[activeFilter].icon
                    }
                    sx={{ fontSize: 48, opacity: 0.5 }}
                  />
                  <Typography color="inherit">
                    {activeFilter === 'all'
                      ? 'Nenhum resultado encontrado para a busca informada.'
                      : `Não foi encontrado ${resultTypeInfos[activeFilter].emptyLabel} para a busca informada.`}
                  </Typography>
                  {!!suggestion && (
                    <S.SuggestionText>
                      Você quis dizer{' '}
                      <S.SuggestionButton
                        type="button"
                        onClick={() => onApplySuggestion?.(suggestion)}
                      >
                        {suggestion}
                      </S.SuggestionButton>
                      ?
                    </S.SuggestionText>
                  )}
                </S.EmptyState>
              )}
            </S.ResultsList>
          </S.ResultsContainer>
        </S.Content>
      </S.Container>
    </Modal>
  )
}

export default SearchModal
